import React, { createRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import {
  ManualViewerV2,
  adaptWidgetHandle,
  applyTranslationOverrides,
  createViewerInstance,
  normalizeInitialView,
  normalizeTimelinePosition,
  ViewerRegistry,
  type Mv2Handle,
  type TranslationOverrides,
  type ManualViewerHandle,
  type InitialView,
  type TimelinePosition,
  type TextOverlayDocument,
  type ViewerInstance,
} from '@xcactus-com/diy-manuals-core';

import { version as EMBED_VERSION } from '../package.json';
import { createBrowserIo } from './analytics/browser';
import { createEmbedAnalyticsTracker, normalizeEndpoint, type EmbedAnalyticsTracker } from './analytics/tracker';
import { bindViewerLifecycle, readAnalyticsBaseUrl } from './analytics/wiring';

export interface DiyViewerMountOptions {
  /** Project shortcode to load. Required. */
  shortcode: string;
  /** Canvas height in pixels. Defaults to 500. */
  viewerHeight?: number;
  /** UI language (BCP-47 tag). Defaults to the browser language. */
  language?: string;
  /**
   * Sparse translation overrides applied before render.
   * Note: operates on the global core i18n instance — affects all viewers on the page.
   * For static pages, prefer `window.Manuals3DTranslations` set before this script loads.
   * For runtime updates, use `window.Manuals3DViewer.applyTranslationOverrides(...)`.
   */
  translations?: TranslationOverrides;
  /** CHG-044: `'right'` (default, container-responsive pill) or `'bottom'`
   *  (horizontal strip below the stage). Invalid → `'right'` with a warning. */
  timelinePosition?: TimelinePosition;
  /** CHG-044: URL of an exported text document, or the parsed document itself. */
  textOverlay?: string | TextOverlayDocument;
  /** CHG-044: language of overlay texts to show (BCP-47). Defaults to `language`. */
  contentLanguage?: string;
  /** CHG-054: `'start'` or `'end'` — this embed's opening scene. Absent = the
   *  project's published setting, else `'start'`. Invalid → inherit with a warning. */
  initialView?: InitialView;
}

const registry = new ViewerRegistry();
const roots = new WeakMap<HTMLElement, Root>();
/** CHG-033: one analytics session per mount, disposed (final flush) with it. */
const trackers = new WeakMap<HTMLElement, EmbedAnalyticsTracker>();

/**
 * CHG-029: the widget mounts inside a SHADOW ROOT on the host's container —
 * host CSS cannot restyle the widget and widget styles never leak out. The
 * component's own <style> tag renders inside the shadow tree. Fonts are the
 * one document-level touch (ensureFonts inside the widget, deduped).
 * A shadow root cannot be detached, so re-mounts reuse it.
 */
function ensureShadowMount(container: HTMLElement, heightPx: number): HTMLElement {
  const shadow = container.shadowRoot ?? container.attachShadow({ mode: 'open' });
  let mountEl = shadow.querySelector<HTMLElement>(':scope > div[data-manuals3d-root]');
  if (!mountEl) {
    mountEl = document.createElement('div');
    mountEl.setAttribute('data-manuals3d-root', '');
    shadow.appendChild(mountEl);
  }
  mountEl.style.width = '100%';
  mountEl.style.height = `${heightPx}px`;
  return mountEl;
}

function mount(container: HTMLElement, options: DiyViewerMountOptions): ViewerInstance | undefined {
  if (!container) {
    console.warn('[Manuals3DViewer] mount called with no container');
    return undefined;
  }
  if (!options?.shortcode) {
    console.warn('[Manuals3DViewer] mount called without a shortcode; nothing rendered');
    return undefined;
  }
  if ('controls' in options) {
    console.warn('[Manuals3DViewer] the `controls` option was removed in 0.10.0 (viewer v2 has no player bar) — ignored');
  }

  if (options.translations) applyTranslationOverrides(options.translations);

  // If a viewer is already mounted on this container, replace it.
  const previous = roots.get(container);
  if (previous) {
    trackers.get(container)?.dispose();
    trackers.delete(container);
    previous.unmount();
    roots.delete(container);
    registry.unregister(container);
  }

  const mountEl = ensureShadowMount(container, options.viewerHeight ?? 500);

  const widgetRef = createRef<Mv2Handle>();
  const handleRef: { current: ManualViewerHandle | null } = {
    get current() {
      return widgetRef.current ? adaptWidgetHandle(widgetRef.current) : null;
    },
    set current(_v) {
      /* read-only adapter — ViewerInstance never assigns */
    },
  };
  const instance = createViewerInstance({
    shortcode: options.shortcode,
    container,
    handleRef,
  });
  const signalReady = (instance as unknown as { _signalReady: () => void })._signalReady.bind(instance);
  const tracker = createEmbedAnalyticsTracker({
    shortcode: options.shortcode,
    endpoint: normalizeEndpoint(readAnalyticsBaseUrl()),
    io: createBrowserIo(),
  });
  const lifecycle = bindViewerLifecycle(tracker, signalReady);

  const timelinePosition = normalizeTimelinePosition(options.timelinePosition, (m) => console.warn(m));
  const initialView = normalizeInitialView(options.initialView, (m) => console.warn(m));

  const root = createRoot(mountEl);
  root.render(
    <React.StrictMode>
      <ManualViewerV2
        ref={widgetRef}
        shortcode={options.shortcode}
        locale={options.language ?? null}
        onReady={lifecycle.onReady}
        onStepAnimationStarted={lifecycle.onStepAnimationStarted}
        onStepAnimationFinished={lifecycle.onStepAnimationFinished}
        timelinePosition={timelinePosition}
        textOverlay={options.textOverlay ?? null}
        contentLanguage={options.contentLanguage ?? null}
        initialView={initialView}
      />
    </React.StrictMode>,
  );

  roots.set(container, root);
  trackers.set(container, tracker);
  registry.register(container, instance);
  return instance;
}

function unmount(container: HTMLElement): void {
  const root = roots.get(container);
  if (!root) return;
  trackers.get(container)?.dispose();
  trackers.delete(container);
  root.unmount();
  roots.delete(container);
  registry.unregister(container);
}

/**
 * Mount `el` if we haven't already. Idempotent — safe to call from autoMount
 * sweeps, the MutationObserver below, or directly. Hosts that need to *force*
 * a re-mount (e.g. shortcode change) should call `window.Manuals3DViewer.mount(...)`
 * which always unmounts the previous root first.
 */
function mountIfNew(el: HTMLElement): void {
  if (roots.has(el)) return;
  const shortcode = el.dataset.shortcode;
  if (!shortcode) {
    console.warn('[Manuals3DViewer] auto-mount element missing data-shortcode', el);
    return;
  }
  const heightAttr = el.dataset.height;
  const parsedHeight = heightAttr ? Number.parseInt(heightAttr, 10) : undefined;
  mount(el, {
    shortcode,
    viewerHeight: Number.isFinite(parsedHeight) ? parsedHeight : undefined,
    language: el.dataset.lang,
    timelinePosition: normalizeTimelinePosition(el.dataset.timelinePosition, (m) => console.warn(m, el)),
    textOverlay: el.dataset.textOverlay,
    contentLanguage: el.dataset.contentLang,
    initialView: normalizeInitialView(el.dataset.initialView, (m) => console.warn(m, el)) ?? undefined,
  });
}

/**
 * Selector matched by autoMount and the MutationObserver.
 * `data-3dmanuals-viewer` is the canonical (and now only) attribute; the
 * `data-diy-viewer` alias was removed in 0.10.0 (CHG-029, absorbing part of
 * CHG-010).
 */
const VIEWER_SELECTOR = '[data-3dmanuals-viewer]';

/** Find every viewer slot on the page and mount a viewer into each. */
function autoMount(): void {
  document.querySelectorAll<HTMLElement>(VIEWER_SELECTOR).forEach(mountIfNew);
}

/**
 * Watch for viewer-slot elements added to the page after initial bootstrap
 * — Shopify sections in particular often lazy-inject their viewer slots
 * from inside an IntersectionObserver callback, well after the script
 * itself has already evaluated and `autoMount` has finished its initial
 * sweep.
 *
 * Cheap: the observer only reacts to childList changes on `document.body`,
 * and inside the callback we do a single `matches()` + `querySelectorAll()`
 * scoped to the added subtree.
 */
function startMutationObserver(): void {
  if (typeof MutationObserver === 'undefined' || !document.body) return;
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches(VIEWER_SELECTOR)) mountIfNew(node);
        node.querySelectorAll<HTMLElement>(VIEWER_SELECTOR).forEach(mountIfNew);
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

interface ManualsViewerGlobal {
  mount: typeof mount;
  unmount: typeof unmount;
  applyTranslationOverrides: typeof applyTranslationOverrides;
  getInstance: (target?: string | HTMLElement) => ViewerInstance | null;
  instances: () => ViewerInstance[];
  version: string;
}

declare global {
  interface Window {
    /**
     * Canonical global from SR-015 onwards.
     */
    Manuals3DViewer?: ManualsViewerGlobal;
    Manuals3DTranslations?: TranslationOverrides;
  }
}

// Expose imperative API for host pages that want to control mount timing
// (e.g. after a SPA route change or when a modal opens).
if (typeof window !== 'undefined') {
  if (window.Manuals3DViewer) {
    // Duplicate <script> on the page (e.g. Shopify section liquid included
    // twice, or a per-section lazy-loader injecting the bundle once per slot).
    // The first IIFE owns `mountedRoots` and the MutationObserver \u2014 those
    // already pick up every viewer slot currently or eventually on the page.
    // Bail (with a hint) so the second IIFE does not register a duplicate
    // observer or stomp on the first IIFE's roots.
    const existing = window.Manuals3DViewer;
    console.warn(
      `[Manuals3DViewer] script loaded more than once (v${EMBED_VERSION} ignored, using v${existing.version}). ` +
      `Multiple includes are tolerated (auto-mount + MutationObserver from the first IIFE handle every viewer on the page), ` +
      `but including embed.js exactly once per page also removes the THREE.WARNING multi-instance noise above.`,
    );
  } else {
    console.info(`\u2234 xcactus // 3D manuals - embed // ${EMBED_VERSION}`);

    // Path 1: pickup translation overrides set by the host page BEFORE this script loaded.
    const initialOverrides = window.Manuals3DTranslations;
    if (initialOverrides) applyTranslationOverrides(initialOverrides);

    // Path 3: expose the override helper at runtime so hosts can update translations
    // after mount (e.g. CMS-driven, A/B test, live preview).
    // `Manuals3DViewer` is the canonical global from SR-015 onwards.
    const api: ManualsViewerGlobal = {
      mount,
      unmount,
      applyTranslationOverrides,
      getInstance: (target) => registry.lookup(target),
      instances: () => registry.list(),
      version: EMBED_VERSION,
    };
    window.Manuals3DViewer = api;

    // Auto-mount any viewer slots already on the page, plus start a
    // MutationObserver so anything added later (Shopify lazy-load pattern,
    // SPA route change) also gets picked up. Hosts can still call
    // window.Manuals3DViewer.mount manually to force a (re)mount with
    // explicit opts.
    const bootstrap = () => { autoMount(); startMutationObserver(); };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
    } else {
      bootstrap();
    }
  }
}

// SR-005/CHG-029: the Vite IIFE wrapper assigns its return value to
// `var Manuals3DViewerBundle` (see vite.config.ts lib.name) — deliberately NOT
// `Manuals3DViewer`, so the wrapper can never clobber the rich `window.Manuals3DViewer`
// API object assigned in the module body above. The top-level exports below only
// feed that wrapper global; `window.Manuals3DViewer` is the canonical surface.
export const version = EMBED_VERSION;
export { mount, unmount, applyTranslationOverrides };
export type { TranslationOverrides };
